import React, { useRef } from 'react';
import { X, Printer } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { formatUiCurrencyDetailed, formatUiDate, formatUiTime } from '../utils/uiFormat';

const OrderReceiptModal = ({ order, settings, onClose }) => {
  const receiptRef = useRef();
  const handlePrint = useReactToPrint({ content: () => receiptRef.current });

  const formatCurrency = (amount) =>
    formatUiCurrencyDetailed(amount, settings?.currency_symbol || 'Rs.');

  if (!order) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-2xl bg-white">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white p-4">
          <h3 className="text-lg font-bold">Sale Details</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div ref={receiptRef} className="p-6">
          <div className="mb-6 text-center">
            <h2 className="text-xl font-bold">{settings?.shop_name || 'Shop'}</h2>
            <p className="text-sm text-gray-600">{settings?.shop_address}</p>
            <p className="text-sm text-gray-600">{settings?.shop_phone}</p>
          </div>

          <div className="mb-4 border-t border-dashed border-gray-300 pt-4">
            <div className="mb-1 flex justify-between text-sm">
              <span>Sale #:</span>
              <span className="font-medium">{order.order_number}</span>
            </div>
            <div className="mb-1 flex justify-between text-sm">
              <span>Date:</span>
              <span>{formatUiDate(order.created_at)}</span>
            </div>
            <div className="mb-1 flex justify-between text-sm">
              <span>Time:</span>
              <span>{formatUiTime(order.created_at)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Cashier:</span>
              <span>{order.cashier_name}</span>
            </div>
          </div>

          <div className="mb-4 border-t border-dashed border-gray-300 pt-4">
            {order.items?.map((item, index) => {
              const modNames = item.modifier_selections?.map((selection) => selection.modifier_option_name).filter(Boolean);
              const displayName = modNames?.length > 0
                ? `${item.menu_item_name} (${modNames.join(', ')})`
                : (item.menu_item_name || 'Item');

              return (
                <div key={index} className="mb-2 flex justify-between text-sm">
                  <div className="mr-2 flex-1">
                    <span className="font-medium">{displayName}</span>
                    <span className="text-gray-500"> x{item.quantity}</span>
                  </div>
                  <span className="flex-shrink-0">{formatCurrency(item.total_price)}</span>
                </div>
              );
            })}
          </div>

          <div className="space-y-2 border-t border-dashed border-gray-300 pt-4">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </div>
            {parseFloat(order.discount_amount) > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-{formatCurrency(order.discount_amount)}</span>
              </div>
            )}
            {parseFloat(order.tax_amount) > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Tax</span>
                <span>{formatCurrency(order.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-gray-300 pt-2 text-lg font-bold">
              <span>Total</span>
              <span>{formatCurrency(order.total_amount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Payment</span>
              <span className="capitalize">{order.payment_method?.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Status</span>
              <span className={`capitalize ${
                order.status === 'completed'
                  ? 'text-green-600'
                  : order.status === 'cancelled'
                    ? 'text-red-600'
                    : 'text-yellow-600'
              }`}
              >
                {order.status}
              </span>
            </div>
          </div>

          <div className="mt-6 border-t border-dashed border-gray-300 pt-4 text-center">
            <p className="text-sm text-gray-600">{settings?.receipt_footer || 'Thank you!'}</p>
          </div>
        </div>

        <div className="flex gap-2 border-t p-4">
          <button
            onClick={handlePrint}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 py-3 font-medium text-white hover:bg-primary-700"
          >
            <Printer size={18} /> Print
          </button>
          <button onClick={onClose} className="flex-1 rounded-lg border border-gray-300 py-3 font-medium hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderReceiptModal;
